import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Checkbox, FormControlLabel, Link, makeStyles, Theme, Typography, } from '@material-ui/core';

import { Page } from '../../components/Page';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { FormGroup } from '../../components/FormGroup';
import { Block } from '../../components/Block';
import { useLoading } from '../../units/loading';
import { PageBody } from '../../components/PageBody';
import { api } from '../../api';
import { useSetting } from '../../units/setting';
import { useQuery } from '../../units/useQuery';
import { useAlert } from '../../units/alert';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { Logo } from '../../components/Logo';
import { useLiff } from '../../units/liff';
import { LoginRequestData } from '../../api/login';
import { useLoginState } from '../../hooks/useLoginState';
import { LiffState } from '../../units/liff/components';
import { DesktopAppBar } from '../../components/DesktopAppBar';
import moment from 'moment';

type LoginPageProps = {};

LoginPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {

    },
    title: {
        margin: theme.spacing(2, 1),
    },
    appBar: {
        display: 'none',
        [theme.breakpoints.up('lg')]: {
            display: 'block',
        },
    },
}));

function LoginPage(props: LoginPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const query = useQuery();
    const loading = useLoading();
    const { setting, setSetting } = useSetting();
    const alert = useAlert();
    const {
        profile,
        isLoadLiff,
        state: liffState,
        error: liffError,
    } = useLiff();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isRemember, setRemember] = useState(setting.loginConfig.isRemember);
    const [isAutoLogin, setAutoLogin] = useState(setting.loginConfig.isAutoLogin);
    const [lineUid, setLineUid] = useState('');
    const { loginState, setLoginState } = useLoginState();

    useEffect(() => {
        if (setting.loginConfig.isRemember) {
            setEmail(setting.loginInfo.login);
        }

        setRemember(setting.loginConfig.isRemember);
        setAutoLogin(setting.loginConfig.isAutoLogin);
    }, [JSON.stringify(setting)]);

    useEffect(() => {
        if (isLoadLiff()) {
            loading.setIsLoading(true);
            if (liffState === LiffState.ProfileLoaded && profile) {
                setLineUid(profile.userId);
                loading.setIsLoading(false);
            }

            if (liffState === LiffState.Error && liffError) {
                loading.setIsLoading(false);
                alert.show({
                    type: 'error',
                    title: '錯誤',
                    message: liffError,
                });
            }
        }
    }, [liffState]);

    useEffect(() => {
        if (isLoadLiff()) {
            if (liffState === LiffState.ProfileLoaded && profile) {
                autoLogin();
            }
        } else {
            autoLogin();
        }
    }, [JSON.stringify(setting), liffState]);

    function autoLogin() {
        if (!setting?.loginInfo.login || !setting?.loginInfo.password) {
            return;
        }

        if (loginState) {
            return;
        }

        if (setting.loginConfig.isAutoLogin) {
            login({
                line_uid: lineUid,
                login: setting.loginInfo.login,
                password: setting.loginInfo.password,
            });
        }
    }

    function login(data: LoginRequestData) {
        loading.setIsLoading(true);
        api.login(data)
            .then(() => {
                loading.setIsLoading(false);
                setSetting({
                    loginInfo: {
                        login: data.login,
                        password: data.password,
                        expiryDate: isAutoLogin ? moment().add(30, 'days').unix() : 0,
                    },
                    loginConfig: {
                        isRemember,
                        isAutoLogin,
                    },
                });
                setLoginState({
                    login: data.login,
                    line_uid: lineUid,
                });

                const redirect = query.get('redirect');
                if (redirect) {
                    history.replace(redirect);
                } else {
                    history.replace('/');
                }
            })
            .catch(async (e) => {
                loading.setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '登入失敗',
                        message: errorData.msg,
                    });
                }
            });
    }

    function handleLoginClick() {
        login({
            line_uid: lineUid,
            login: email,
            password,
        });
    }

    return (
        <Page className={classes.root}>
            <DesktopAppBar className={classes.appBar} />
            <PageBody>
                <Logo />
                <Typography className={classes.title} variant="h5" align="center"></Typography>
                <FormGroup>
                    <TextField
                        label="帳號"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="new-password"
                        onEnter={handleLoginClick}
                    />
                    <TextField
                        label="密碼"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                        onEnter={handleLoginClick}
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={isRemember}
                                onChange={(event, checked) => setRemember(checked)}
                                color="primary"
                            />
                        )}
                        label="記住帳號"
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={isAutoLogin}
                                onChange={(event, checked) => setAutoLogin(checked)}
                                color="primary"
                            />
                        )}
                        label="30天內自動登入"
                    />
                    <Button onClick={handleLoginClick}>會員登入</Button>
                    <Block row justifyContent="space-between">
                        <Link component="a" onClick={() => history.push('/register')}>註冊帳號</Link>
                        <Link component="a" onClick={() => history.push('/forgetPassword')}>忘記密碼?</Link>
                    </Block>
                </FormGroup>
            </PageBody>
        </Page>
    );
}

export { LoginPage };
